import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ShoppingListItem } from '../types';
import { todayKey, addDays } from '../lib/dateUtils';
import { useTier } from '../context/AuthContext';
import { lavenderButtonStyle } from '../lib/theme';
import Spinner from '../components/Spinner';
import EmptyState from '../components/EmptyState';
import emptyShoppingListImage from '../assets/empty-shopping-list.png';

const CURSIVE_FONT = '"Caveat", cursive';

export default function ShoppingList() {
  const tier = useTier();
  const start = todayKey();
  const end = addDays(start, 6);
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['shopping-list', start, end],
    queryFn: () => api.get<{ items: ShoppingListItem[] }>(`/shopping-list?start=${start}&end=${end}`),
    enabled: tier === 'PRO',
  });
  const items = data?.items ?? [];

  function toggle(item: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(item)) next.delete(item);
      else next.add(item);
      return next;
    });
  }

  if (tier !== 'PRO') {
    return (
      <div>
        <h2>Shopping list</h2>
        <p style={{ opacity: 0.75, maxWidth: 480 }}>
          The shopping list is a PRO feature — it's automatically built from everything you log during the week.
        </p>
        <Link to="/upgrade">
          <button style={lavenderButtonStyle}>See PRO features</button>
        </Link>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: CURSIVE_FONT }}>
      <h2 style={{ fontSize: '2.2rem', margin: '0 0 1rem' }}>Shopping list</h2>

      {isLoading ? (
        <Spinner label="Loading…" />
      ) : items.length === 0 ? (
        <EmptyState image={emptyShoppingListImage} alt="No meals planned in this range yet" />
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {items.map((item) => (
            <li key={item.item} style={{ padding: '0.3rem 0', fontSize: '1.4rem' }}>
              <label style={{ textDecoration: checked.has(item.item) ? 'line-through' : 'none' }}>
                <input type="checkbox" checked={checked.has(item.item)} onChange={() => toggle(item.item)} />{' '}
                {item.item} {item.count > 1 ? `(x${item.count})` : ''}
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
